/** Builds the guided-tour steps from the menu items the viewer actually has,
 *  so each role is toured through its own app rather than a generic script.
 *
 *  BODY_COPY is a total Record over NavKey — adding a menu item without an
 *  explanation is a compile error, which is the point: the client's feedback
 *  was that every item needs to say what it does. */
import type { NavItem, NavKey } from "../layout/AppShell";
import type { MessageKey } from "../../i18n/en";
import type { TourStep } from "./ProductTour";

/** What each menu item is for, one line each.
 *
 *  Exported because the Help screen explains the same menus and must not
 *  drift from the tour — the client asked for both, and two descriptions of
 *  one menu that disagree is worse than either alone. Total over NavKey, so
 *  adding an item without an explanation is a compile error. */
export const BODY_COPY: Record<NavKey, MessageKey> = {
  "nav.dashboard": "tour.body.dashboard",
  "nav.learn": "tour.body.learn",
  "nav.reference": "tour.body.reference",
  "nav.notebook": "tour.body.notebook",
  "nav.history": "tour.body.history",
  "nav.payments": "tour.body.payments",
  "nav.store": "tour.body.store",
  "nav.practice": "tour.body.practice",
  "nav.speak": "tour.body.speak",
  "nav.team": "tour.body.team",
  "nav.classes": "tour.body.classes",
  "nav.access": "tour.body.access",
  "nav.billing": "tour.body.billing",
  "nav.tenants": "tour.body.tenants",
  "nav.orders": "tour.body.orders",
  "nav.revenue": "tour.body.revenue",
  "nav.ai": "tour.body.ai",
  "nav.aiUsage": "tour.body.aiUsage",
};

type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

export function buildTourSteps(t: Translate, items: NavItem[]): TourStep[] {
  return [
    { title: t("tour.welcome.title"), body: t("tour.welcome.body") },
    ...items.map((item) => ({
      // Either the item's own link, or - when it lives inside a closed
      // dropdown - the visible group button that opens it. The trigger
      // comes first in document order, so querySelector picks it.
      target: `[data-tour="${item.key}"], [data-tour-group~="${item.key}"]`,
      title: t(item.key),
      body: t(BODY_COPY[item.key]),
    })),
    // Not a menu item, so it is added by hand — and it has to be here,
    // because "how would a student ever know about this?" was the client's
    // question about it.
    {
      target: '[data-tour="ask.doubt"]',
      title: t("tour.askDoubt.title"),
      body: t("tour.askDoubt.body"),
    },
    { title: t("tour.final.title"), body: t("tour.final.body") },
  ];
}
